import type { HttpClient } from '../client';
import type { Id } from '@hq/utils';
import type {
  AppDeleteResult,
  AppPlatform,
  AppReleasePatchRequest,
  AppReleaseRecordRequest,
  AppReleaseRecordResult,
  AppReleaseRow,
  AppSetCurrentResult,
  AppVersionRow,
  AppVersionSaveRequest,
  AppVersionSaveResult,
  CheckUpdateRequest,
  CheckUpdateResult,
  QrAttachRequest
} from '../types/appVersion';

const BASE = '/sys/app-version';

/**
 * App 版本管理（web-system `/system/app-versions`）：
 * 版本配置（sys_app_version）+ 安装包发布元数据登记（sys_app_release）。
 * 文件（安装包/二维码/logo）由 web-system 站点本地存到 apps 目录（见站点 src/appsStore.ts），
 * 本 API 只收发元数据。checkUpdate 为匿名端点（/api/app/check-update），供 App 端检查更新调用。
 */
export function createAppVersionApi(http: HttpClient) {
  return {
    version: {
      list(keyword = ''): Promise<AppVersionRow[]> {
        return http.request<AppVersionRow[]>({ path: `${BASE}/list`, query: { keyword } });
      },
      save(body: AppVersionSaveRequest): Promise<AppVersionSaveResult> {
        return http.request<AppVersionSaveResult>({ path: BASE, method: 'POST', body });
      },
      remove(id: Id): Promise<AppDeleteResult> {
        return http.request<AppDeleteResult>({ path: `${BASE}/${id}`, method: 'DELETE' });
      }
    },

    release: {
      list(appKey = '', platform: AppPlatform | '' = ''): Promise<AppReleaseRow[]> {
        return http.request<AppReleaseRow[]>({ path: `${BASE}/release/list`, query: { appKey, platform } });
      },
      /** 登记发布（纯元数据 JSON）；文件已前端本地落地，fileName/fileUrl 为落地后真实值（安卓必填）。 */
      record(body: AppReleaseRecordRequest): Promise<AppReleaseRecordResult> {
        return http.request<AppReleaseRecordResult>({ path: `${BASE}/release`, method: 'POST', body });
      },
      setCurrent(id: Id): Promise<AppSetCurrentResult> {
        return http.request<AppSetCurrentResult>({ path: `${BASE}/release/${id}/set-current`, method: 'POST' });
      },
      /** 为已登记发布补挂二维码（前端已本地存 PNG，仅上报文件名+地址）。 */
      attachQr(id: Id, body: QrAttachRequest): Promise<{ id: Id; qrFileName: string; qrUrl: string }> {
        return http.request<{ id: Id; qrFileName: string; qrUrl: string }>({ path: `${BASE}/release/${id}/qr`, method: 'POST', body });
      },
      patch(id: Id, body: AppReleasePatchRequest): Promise<{ id: Id; updated: boolean }> {
        return http.request<{ id: Id; updated: boolean }>({ path: `${BASE}/release/${id}`, method: 'PATCH', body });
      },
      remove(id: Id): Promise<AppDeleteResult> {
        return http.request<AppDeleteResult>({ path: `${BASE}/release/${id}`, method: 'DELETE' });
      }
    },

    /** 端侧检查更新（匿名，无鉴权也能调）。 */
    checkUpdate(body: CheckUpdateRequest): Promise<CheckUpdateResult> {
      return http.request<CheckUpdateResult>({ path: '/app/check-update', method: 'POST', body });
    }
  };
}

export type AppVersionApi = ReturnType<typeof createAppVersionApi>;
